import { useState, useEffect } from 'react';
import { apiClient } from '../services/apiClient';
import { useToast } from '../components/Toast';

interface LicenseStats {
  totalUsers: number;
  licensedUsers: number;
  maxLicenses: number;
  availableLicenses: number;
  directLicenses: number;
  groupLicenses: number;
}

interface LicensedUser {
  id: string;
  email: string;
  name: string | null;
  isLicensed: boolean;
  licenseSource: 'DIRECT' | 'GROUP' | null;
  licensedViaGroupId: string | null;
  licensedViaGroupName: string | null;
  licensedAt: Date | null;
  licensedBy: string | null;
}

interface UnlicensedUser {
  id: string;
  email: string;
  displayName: string;
}

interface LicensedGroup {
  id: string;
  groupId: string;
  groupName: string;
  groupDescription: string | null;
  memberCount: number;
  assignedAt: Date;
  assignedBy: string | null;
}

interface AvailableGroup {
  id: string;
  displayName: string;
  description: string | null;
}

export default function Licenses() {
  const toast = useToast();
  const [tenantId, setTenantId] = useState<string>('');
  const [stats, setStats] = useState<LicenseStats | null>(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState<any>(null);
  const [licensedUsers, setLicensedUsers] = useState<LicensedUser[]>([]);
  const [licensedGroups, setLicensedGroups] = useState<LicensedGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [showAssignUserModal, setShowAssignUserModal] = useState(false);
  const [showAssignGroupModal, setShowAssignGroupModal] = useState(false);
  const [unlicensedUsers, setUnlicensedUsers] = useState<UnlicensedUser[]>([]);
  const [availableGroups, setAvailableGroups] = useState<AvailableGroup[]>([]);
  const [loadingUnlicensed, setLoadingUnlicensed] = useState(false);
  const [loadingAvailableGroups, setLoadingAvailableGroups] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [groupSearch, setGroupSearch] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [processing, setProcessing] = useState(false);
  const [showMaxLicensesModal, setShowMaxLicensesModal] = useState(false);
  const [maxLicensesInput, setMaxLicensesInput] = useState<number>(100);
  const [showGroupMembersModal, setShowGroupMembersModal] = useState(false);
  const [selectedGroupMembers, setSelectedGroupMembers] = useState<any[]>([]);
  const [selectedGroupName, setSelectedGroupName] = useState('');
  const [loadingGroupMembers, setLoadingGroupMembers] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('Processing...');
  const [gracePeriodStatus, setGracePeriodStatus] = useState<any>(null);

  // Get current user's tenant on mount
  useEffect(() => {
    apiClient.get('/api/auth/me')
      .then((data) => {
        if (data.success) {
          setTenantId(data.data.tenantId);
        }
      })
      .catch((error) => console.error('Failed to get user info:', error));
  }, []);

  // Load stats, licensed users and groups when tenant changes
  useEffect(() => {
    if (tenantId) {
      loadStats();
      loadSubscriptionInfo();
      loadLicensedUsers();
      loadLicensedGroups();
      loadGracePeriodStatus();
    }
  }, [tenantId]);

  const loadStats = async () => {
    try {
      const data = await apiClient.get('/api/user-licenses/stats');
      if (data.success) {
        setStats(data.data);
        setMaxLicensesInput(data.data.maxLicenses);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadSubscriptionInfo = async () => {
    try {
      const data = await apiClient.get(`/api/license/tenant/${tenantId}`);
      if (data.success) {
        setSubscriptionInfo(data.data);
      }
    } catch (error) {
      console.error('Failed to load subscription info:', error);
    }
  };

  const loadGracePeriodStatus = async () => {
    try {
      const data = await apiClient.get('/api/user-licenses/grace-period');
      if (data.success) {
        setGracePeriodStatus(data.data);
      }
    } catch (error) {
      console.error('Failed to load grace period status:', error);
    }
  };

  const loadLicensedUsers = async () => {
    setLoading(true);
    try {
      const data = await apiClient.get('/api/user-licenses/users');
      if (data.success) {
        // Only show users with DIRECT licenses
        const directLicenses = data.data.filter((user: LicensedUser) => user.licenseSource === 'DIRECT');
        setLicensedUsers(directLicenses);
      }
    } catch (error) {
      console.error('Failed to load licensed users:', error);
    }
    setLoading(false);
  };

  const loadLicensedGroups = async () => {
    setLoadingGroups(true);
    try {
      const data = await apiClient.get('/api/user-licenses/groups');
      if (data.success) {
        setLicensedGroups(data.data);
      }
    } catch (error) {
      console.error('Failed to load licensed groups:', error);
    }
    setLoadingGroups(false);
  };

  const loadUnlicensedUsers = async () => {
    setLoadingUnlicensed(true);
    try {
      // Fetch users from Microsoft 365 (filters out guests)
      const data = await apiClient.get(`/api/users/available/${tenantId}`);
      if (data.success) {
        setUnlicensedUsers(data.data);
      }
    } catch (error) {
      console.error('Failed to load unlicensed users:', error);
    }
    setLoadingUnlicensed(false);
  };

  const loadAvailableGroups = async () => {
    setLoadingAvailableGroups(true);
    try {
      // Fetch groups from Microsoft 365
      const data = await apiClient.get(`/api/groups/available/${tenantId}`);
      if (data.success) {
        setAvailableGroups(data.data);
      }
    } catch (error) {
      console.error('Failed to load available groups:', error);
    }
    setLoadingAvailableGroups(false);
  };

  const handleOpenAssignUserModal = () => {
    setShowAssignUserModal(true);
    loadUnlicensedUsers();
  };

  const handleOpenAssignGroupModal = () => {
    setShowAssignGroupModal(true);
    loadAvailableGroups();
  };

  const toggleUserSelection = (userId: string) => {
    const newSelected = new Set(selectedUserIds);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUserIds(newSelected);
  };

  const handleAssignLicenses = async () => {
    if (selectedUserIds.size === 0) {
      toast.error('Please select at least one user');
      return;
    }

    setProcessingMessage('Syncing users...');
    setProcessing(true);
    const errors: string[] = [];
    const dbUserIds: string[] = [];

    // Sync each selected user individually
    for (const graphUserId of selectedUserIds) {
      try {
        const user = unlicensedUsers.find(u => u.id === graphUserId);
        const userName = user?.displayName || graphUserId;

        // Sync this specific user to database
        const syncResponse = await apiClient.post(`/api/users/sync-single`, {
          tenantId,
          graphUserId,
        });
        
        if (!syncResponse.success) {
          errors.push(`${userName}: Failed to sync`);
          continue;
        }

        // Get the database user ID from the sync response
        dbUserIds.push(syncResponse.data.user.id);
      } catch (error: any) {
        const user = unlicensedUsers.find(u => u.id === graphUserId);
        const userName = user?.displayName || graphUserId;
        errors.push(`${userName}: ${error.message}`);
      }
    }

    if (dbUserIds.length === 0) {
      toast.error('No users could be synced for licensing');
      setProcessing(false);
      return;
    }

    // Now assign licenses using database IDs
    try {
      const data = await apiClient.post('/api/user-licenses/assign', {
        userIds: dbUserIds,
      });

      if (data.success) {
        const message = errors.length > 0
          ? `Assigned licenses to ${data.data.assigned} user(s). ${errors.length} failed.`
          : `Assigned licenses to ${data.data.assigned} user(s)`;
        toast.success(message);
        if (errors.length > 0) {
          console.error('Assignment errors:', errors);
        }
        setShowAssignUserModal(false);
        setSelectedUserIds(new Set());
        await loadStats();
        await loadLicensedUsers();
        await loadGracePeriodStatus();
      } else {
        toast.error(`Failed: ${data.error?.message || 'Unknown error'}`);
      }
    } catch (error: any) {
      toast.error(`Failed to assign licenses: ${error.message}`);
    }
    setProcessing(false);
  };

  const handleRevokeLicense = async (userId: string) => {
    setProcessingMessage('Revoking license...');
    setProcessing(true);
    try {
      const data = await apiClient.post('/api/user-licenses/revoke', {
        userIds: [userId],
      });

      if (data.success) {
        toast.success('License revoked');
        await loadStats();
        await loadLicensedUsers();
        await loadGracePeriodStatus();
      } else {
        toast.error(`Failed: ${data.error?.message || 'Unknown error'}`);
      }
    } catch (error: any) {
      toast.error(`Failed to revoke license: ${error.message}`);
    }
    setProcessing(false);
  };

  const handleAssignGroupLicense = async (graphGroupId: string, groupName: string) => {
    setProcessingMessage('Syncing group members...');
    setProcessing(true);
    try {
      // First sync the group to ensure it exists in database
      const syncData = await apiClient.post('/api/groups/sync-single', {
        tenantId,
        graphGroupId,
      });

      if (!syncData.success) {
        toast.error(`Failed to sync group: ${syncData.error}`);
        setProcessing(false);
        return;
      }

      // Assign group license using the group ID directly from sync response
      const data = await apiClient.post('/api/user-licenses/groups/assign', {
        groupId: syncData.data.group.id,
      });

      if (data.success) {
        toast.success(`Assigned license to group: ${groupName}`);
        setShowAssignGroupModal(false);
        await loadStats();
        await loadLicensedUsers();
        await loadLicensedGroups();
        await loadAvailableGroups();
        await loadGracePeriodStatus();
      } else {
        toast.error(`Failed: ${data.error?.message || 'Unknown error'}`);
      }
    } catch (error: any) {
      toast.error(`Failed to assign group license: ${error.message}`);
    }
    setProcessing(false);
  };

  const handleUnassignGroupLicense = async (groupId: string, groupName: string) => {
    setProcessingMessage('Unassigning group license...');
    setProcessing(true);
    try {
      const data = await apiClient.post('/api/user-licenses/groups/unassign', {
        groupId,
      });

      if (data.success) {
        toast.success(`Unassigned license from group: ${groupName}`);
        await loadStats();
        await loadLicensedUsers();
        await loadLicensedGroups();
        await loadGracePeriodStatus();
      } else {
        toast.error(`Failed: ${data.error?.message || 'Unknown error'}`);
      }
    } catch (error: any) {
      toast.error(`Failed to unassign group license: ${error.message}`);
    }
    setProcessing(false);
  };

  const handleManageBilling = async () => {
    try {
      const data = await apiClient.post('/api/license/customer-portal');
      if (data.success) {
        // Redirect to Stripe Customer Portal
        window.location.href = data.data.url;
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to open billing portal');
    }
  };

  const handleViewGroupMembers = async (groupId: string, groupName: string) => {
    setSelectedGroupName(groupName);
    setShowGroupMembersModal(true);
    setLoadingGroupMembers(true);
    
    try {
      const data = await apiClient.get(`/api/groups/details/${groupId}`);
      if (data.success) {
        setSelectedGroupMembers(data.data.members || []);
      }
    } catch (error) {
      console.error('Failed to load group members:', error);
      toast.error('Failed to load group members');
    }
    setLoadingGroupMembers(false);
  };

  const handleUpdateMaxLicenses = async () => {
    if (maxLicensesInput < 100) {
      toast.error('Maximum licenses must be at least 100');
      return;
    }

    setProcessingMessage('Updating maximum licenses...');
    setProcessing(true);
    try {
      const data = await apiClient.post('/api/user-licenses/max-licenses', {
        maxLicenses: maxLicensesInput,
      });

      if (data.success) {
        toast.success('Maximum licenses updated');
        setShowMaxLicensesModal(false);
        await loadStats();
        await loadGracePeriodStatus();
      } else {
        toast.error(`Failed: ${data.error?.message || 'Unknown error'}`);
      }
    } catch (error: any) {
      toast.error(`Failed to update: ${error.message}`);
    }
    setProcessing(false);
  };

  const filteredUnlicensedUsers = userSearch
    ? unlicensedUsers.filter(
        (user) =>
          user.displayName?.toLowerCase().includes(userSearch.toLowerCase()) ||
          user.email.toLowerCase().includes(userSearch.toLowerCase())
      )
    : unlicensedUsers;

  const filteredAvailableGroups = groupSearch
    ? availableGroups.filter(
        (group) =>
          group.displayName.toLowerCase().includes(groupSearch.toLowerCase()) ||
          group.description?.toLowerCase().includes(groupSearch.toLowerCase())
      )
    : availableGroups;

  // Get set of already licensed group names to filter them out
  const licensedGroupNames = new Set(licensedGroups.map(g => g.groupName));

  const usagePercentage = stats
    ? Math.round((stats.licensedUsers / stats.maxLicenses) * 100)
    : 0;

  return (
    <div>
      <div style={{ marginBottom: '30px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ margin: '0 0 10px' }}>License Management</h2>
            <p style={{ color: '#666', margin: 0 }}>
              Manage user and group licenses
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={handleManageBilling}
              style={{
                padding: '8px 16px',
                background: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
              }}
            >
              💳 Manage Billing
            </button>
            <button
              onClick={() => setShowMaxLicensesModal(true)}
              style={{
                padding: '8px 16px',
                background: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
              }}
            >
              ⚙️ Settings
            </button>
          </div>
        </div>
      </div>

      {/* Grace Period Warning Banner */}
      {gracePeriodStatus?.isOverLimit && gracePeriodStatus?.gracePeriod && (
        <div style={{
          marginBottom: '20px',
          background: gracePeriodStatus.gracePeriod.isExpired ? '#fee2e2' : '#fef3c7',
          padding: '20px',
          borderRadius: '8px',
          border: `2px solid ${gracePeriodStatus.gracePeriod.isExpired ? '#ef4444' : '#f59e0b'}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
            <div style={{ fontSize: '24px' }}>
              {gracePeriodStatus.gracePeriod.isExpired ? '🚨' : '⚠️'}
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: '0 0 10px', fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
                {gracePeriodStatus.gracePeriod.isExpired ? 'License Limit Exceeded - Grace Period Expired' : 'License Limit Exceeded'}
              </h3>
              <p style={{ margin: '0 0 10px', fontSize: '14px', color: '#4b5563' }}>
                You have <strong>{gracePeriodStatus.currentCount}</strong> licensed users but your limit is <strong>{gracePeriodStatus.maxLicenses}</strong>.
                {' '}<strong>{gracePeriodStatus.overCount} users</strong> are over the limit.
              </p>
              {gracePeriodStatus.gracePeriod.isExpired ? (
                <p style={{ margin: '0 0 15px', fontSize: '14px', color: '#991b1b', fontWeight: '600' }}>
                  ⚠️ Grace period expired! The {gracePeriodStatus.overCount} newest licensed users cannot access their signatures.
                </p>
              ) : (
                <p style={{ margin: '0 0 15px', fontSize: '14px', color: '#92400e', fontWeight: '500' }}>
                  Grace period: <strong>{gracePeriodStatus.gracePeriod.daysRemaining} days remaining</strong>.
                  After that, the {gracePeriodStatus.overCount} newest licensed users will be blocked from accessing their signatures.
                </p>
              )}
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => setShowMaxLicensesModal(true)}
                  style={{
                    padding: '10px 20px',
                    background: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  Increase License Limit
                </button>
                <button
                  onClick={handleManageBilling}
                  style={{
                    padding: '10px 20px',
                    background: 'white',
                    color: '#1f2937',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  Manage Billing
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Status Widget */}
      {subscriptionInfo && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{
            background: subscriptionInfo.status === 'TRIAL' ? '#fef3c7' : subscriptionInfo.status === 'ACTIVE' ? '#d1fae5' : '#fee2e2',
            padding: '20px',
            borderRadius: '8px',
            border: `2px solid ${subscriptionInfo.status === 'TRIAL' ? '#f59e0b' : subscriptionInfo.status === 'ACTIVE' ? '#059669' : '#ef4444'}`,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <div>
                <h3 style={{ margin: '0 0 5px', fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
                  {subscriptionInfo.status === 'TRIAL' && 'Trial Period'}
                  {subscriptionInfo.status === 'ACTIVE' && 'Active Subscription'}
                  {subscriptionInfo.status === 'EXPIRED' && 'License Expired'}
                  {subscriptionInfo.status === 'SUSPENDED' && 'Suspended'}
                </h3>
                {subscriptionInfo.status === 'TRIAL' && subscriptionInfo.daysRemaining !== null && (
                  <p style={{ margin: 0, fontSize: '14px', color: '#92400e', fontWeight: '500' }}>
                    {subscriptionInfo.daysRemaining} days remaining
                  </p>
                )}
              </div>
              {(subscriptionInfo.status === 'TRIAL' || subscriptionInfo.status === 'EXPIRED') && (
                <button
                  style={{
                    padding: '10px 20px',
                    background: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                  onClick={async () => {
                    try {
                      const data = await apiClient.post('/api/license/create-checkout', { tenantId });
                      if (data.success) {
                        window.location.href = data.data.url;
                      } else {
                        toast.error('Failed to create checkout session');
                      }
                    } catch (error) {
                      toast.error('Failed to initiate checkout');
                    }
                  }}
                >
                  Upgrade Now
                </button>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>Users</div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#1f2937' }}>{subscriptionInfo.userCount}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>Monthly Price</div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#1f2937' }}>{subscriptionInfo.monthlyPrice} DKK</div>
              </div>
              {subscriptionInfo.nextBillingDate && (
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>Next Billing</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                    {new Date(subscriptionInfo.nextBillingDate).toLocaleDateString()}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* License Overview Card */}
      {stats && (
        <div style={{
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          padding: '24px',
          marginBottom: '20px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>License Overview</h3>
            <span style={{ fontSize: '24px' }}>🛡️</span>
          </div>

          <div>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '8px' }}>
                <span style={{ fontWeight: '500' }}>
                  {stats.licensedUsers} / {stats.maxLicenses} licenses in use
                </span>
                <span style={{ color: '#666' }}>{usagePercentage}%</span>
              </div>
              <div style={{ width: '100%', height: '12px', background: '#e5e7eb', borderRadius: '6px', overflow: 'hidden' }}>
                <div
                  style={{
                    height: '100%',
                    background: usagePercentage >= 90 ? '#dc2626' : usagePercentage >= 75 ? '#eab308' : '#2563eb',
                    width: `${Math.min(usagePercentage, 100)}%`,
                    transition: 'width 0.3s',
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
              <div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>
                  {stats.availableLicenses}
                </div>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>Available</div>
              </div>
              <div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2563eb' }}>
                  {stats.directLicenses}
                </div>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>Direct</div>
              </div>
              <div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#9333ea' }}>
                  {stats.groupLicenses}
                </div>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>Via Groups</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Two Column Layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {/* Left Column: Licensed Groups */}
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
              Licensed Groups ({licensedGroups.length})
            </h3>
            <button
              onClick={handleOpenAssignGroupModal}
              style={{
                padding: '6px 12px',
                background: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500',
              }}
            >
              + Assign Group
            </button>
          </div>

          <div style={{
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            maxHeight: '600px',
            overflowY: 'auto',
          }}>
            {loadingGroups ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                Loading licensed groups...
              </div>
            ) : licensedGroups.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                No licensed groups yet. Assign groups to automatically license all members.
              </div>
            ) : (
              licensedGroups.map((group) => (
                <div
                  key={group.id}
                  style={{
                    padding: '15px 20px',
                    borderBottom: '1px solid #e5e7eb',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div 
                    style={{ flex: 1, cursor: 'pointer' }}
                    onClick={() => handleViewGroupMembers(group.groupId, group.groupName)}
                  >
                    <div style={{ fontWeight: '500', color: '#1f2937', marginBottom: '4px' }}>
                      {group.groupName}
                    </div>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>
                      {group.memberCount} member{group.memberCount !== 1 ? 's' : ''}
                      {group.groupDescription && ` • ${group.groupDescription}`}
                    </div>
                  </div>
                  <button
                    onClick={() => handleUnassignGroupLicense(group.groupId, group.groupName)}
                    disabled={processing}
                    style={{
                      padding: '6px 12px',
                      background: '#fee2e2',
                      color: '#dc2626',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: processing ? 'not-allowed' : 'pointer',
                      fontSize: '13px',
                      fontWeight: '500',
                      opacity: processing ? 0.5 : 1,
                    }}
                  >
                    Unassign
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right Column: Licensed Users */}
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
              Licensed Users ({licensedUsers.length})
            </h3>
            <button
              onClick={handleOpenAssignUserModal}
              style={{
                padding: '6px 12px',
                background: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500',
              }}
            >
              + Assign User
            </button>
          </div>

          <div style={{
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            maxHeight: '600px',
            overflowY: 'auto',
          }}>
            {loading ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                Loading licensed users...
              </div>
            ) : licensedUsers.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                No direct user licenses. Use groups or assign users individually.
              </div>
            ) : (
              licensedUsers.map((user) => (
                <div
                  key={user.id}
                  style={{
                    padding: '15px 20px',
                    borderBottom: '1px solid #e5e7eb',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '500', color: '#1f2937', marginBottom: '4px' }}>
                      {user.name || 'Unnamed User'}
                    </div>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>
                      {user.email}
                    </div>
                  </div>
                  <button
                    onClick={() => handleRevokeLicense(user.id)}
                    disabled={processing}
                    style={{
                      padding: '6px 12px',
                      background: '#fee2e2',
                      color: '#dc2626',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: processing ? 'not-allowed' : 'pointer',
                      fontSize: '13px',
                      fontWeight: '500',
                      opacity: processing ? 0.5 : 1,
                    }}
                  >
                    Revoke
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Assign User Modal */}
      {showAssignUserModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
          }}
          onClick={() => setShowAssignUserModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '8px',
              width: '600px',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e5e7eb',
            }}>
              <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '600' }}>
                Assign Licenses to Users
              </h3>
              <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                Select users to assign direct licenses
              </p>
            </div>

            <div style={{ padding: '20px', borderBottom: '1px solid #e5e7eb' }}>
              <input
                type="text"
                placeholder="Search users..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                }}
              />
            </div>

            <div style={{ flex: 1, overflowY: 'auto', maxHeight: '400px' }}>
              {loadingUnlicensed ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                  Loading users...
                </div>
              ) : filteredUnlicensedUsers.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                  {userSearch ? 'No users found' : 'All users are already licensed'}
                </div>
              ) : (
                filteredUnlicensedUsers.map((user) => (
                  <div
                    key={user.id}
                    style={{
                      padding: '12px 20px',
                      borderBottom: '1px solid #e5e7eb',
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      background: selectedUserIds.has(user.id) ? '#f3f4f6' : 'white',
                    }}
                    onClick={() => toggleUserSelection(user.id)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedUserIds.has(user.id)}
                      onChange={() => {}}
                      style={{ marginRight: '12px' }}
                    />
                    <div>
                      <div style={{ fontWeight: '500', color: '#1f2937', fontSize: '14px' }}>
                        {user.displayName}
                      </div>
                      <div style={{ fontSize: '13px', color: '#6b7280' }}>
                        {user.email}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div style={{
              padding: '20px',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
            }}>
              <button
                onClick={() => {
                  setShowAssignUserModal(false);
                  setSelectedUserIds(new Set());
                }}
                style={{
                  padding: '8px 16px',
                  background: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAssignLicenses}
                disabled={processing || selectedUserIds.size === 0}
                style={{
                  padding: '8px 16px',
                  background: selectedUserIds.size > 0 ? '#2563eb' : '#9ca3af',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: selectedUserIds.size > 0 && !processing ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                {processing ? 'Assigning...' : `Assign to ${selectedUserIds.size} user(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Group Modal */}
      {showAssignGroupModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
          }}
          onClick={() => setShowAssignGroupModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '8px',
              width: '600px',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e5e7eb',
            }}>
              <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '600' }}>
                Assign License to Group
              </h3>
              <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                All group members will automatically receive licenses
              </p>
            </div>

            <div style={{ padding: '20px', borderBottom: '1px solid #e5e7eb' }}>
              <input
                type="text"
                placeholder="Search groups..."
                value={groupSearch}
                onChange={(e) => setGroupSearch(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                }}
              />
            </div>

            <div style={{ flex: 1, overflowY: 'auto', maxHeight: '400px' }}>
              {loadingAvailableGroups ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                  Loading groups...
                </div>
              ) : filteredAvailableGroups.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                  {groupSearch ? 'No groups found' : 'All groups are already licensed'}
                </div>
              ) : (
                filteredAvailableGroups.map((group) => {
                  const alreadyLicensed = licensedGroupNames.has(group.displayName);
                  return (
                    <div
                      key={group.id}
                      style={{
                        padding: '15px 20px',
                        borderBottom: '1px solid #e5e7eb',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        opacity: alreadyLicensed ? 0.6 : 1,
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '500', color: '#1f2937', marginBottom: '4px' }}>
                          {group.displayName}
                          {alreadyLicensed && <span style={{ marginLeft: '8px', color: '#059669', fontSize: '13px', fontWeight: '600' }}>✓ Licensed</span>}
                        </div>
                        <div style={{ fontSize: '13px', color: '#6b7280' }}>
                          {group.description || 'No description'}
                        </div>
                      </div>
                      {alreadyLicensed ? (
                        <div style={{ fontSize: '13px', color: '#6b7280', fontStyle: 'italic' }}>Already licensed</div>
                      ) : (
                        <button
                          onClick={() => handleAssignGroupLicense(group.id, group.displayName)}
                          disabled={processing}
                          style={{
                            padding: '6px 12px',
                            background: '#2563eb',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: processing ? 'not-allowed' : 'pointer',
                            fontSize: '13px',
                            fontWeight: '500',
                            opacity: processing ? 0.5 : 1,
                          }}
                        >
                          Assign
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            <div style={{
              padding: '20px',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'flex-end',
            }}>
              <button
                onClick={() => setShowAssignGroupModal(false)}
                style={{
                  padding: '8px 16px',
                  background: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Max Licenses Modal */}
      {showMaxLicensesModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
          }}
          onClick={() => setShowMaxLicensesModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '8px',
              width: '400px',
              padding: '24px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px', fontSize: '18px', fontWeight: '600' }}>
              Update Maximum Licenses
            </h3>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Maximum Licenses (minimum 100)
              </label>
              <input
                type="number"
                min="100"
                value={maxLicensesInput}
                onChange={(e) => setMaxLicensesInput(parseInt(e.target.value))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                }}
              />
              {stats && (
                <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#6b7280' }}>
                  Currently using {stats.licensedUsers} licenses
                </p>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => setShowMaxLicensesModal(false)}
                style={{
                  padding: '8px 16px',
                  background: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateMaxLicenses}
                disabled={processing}
                style={{
                  padding: '8px 16px',
                  background: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: processing ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: processing ? 0.5 : 1,
                }}
              >
                {processing ? 'Updating...' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Group Members Modal */}
      {showGroupMembersModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
          }}
          onClick={() => setShowGroupMembersModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '8px',
              width: '600px',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e5e7eb',
            }}>
              <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '600' }}>
                {selectedGroupName} - Members
              </h3>
              <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                All members of this group are automatically licensed
              </p>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', maxHeight: '500px' }}>
              {loadingGroupMembers ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                  Loading members...
                </div>
              ) : selectedGroupMembers.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                  No members in this group
                </div>
              ) : (
                selectedGroupMembers.map((member: any) => (
                  <div
                    key={member.id}
                    style={{
                      padding: '12px 20px',
                      borderBottom: '1px solid #e5e7eb',
                    }}
                  >
                    <div style={{ fontWeight: '500', color: '#1f2937', fontSize: '14px', marginBottom: '2px' }}>
                      {member.user.displayName}
                    </div>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>
                      {member.user.email}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div style={{
              padding: '20px',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'flex-end',
            }}>
              <button
                onClick={() => setShowGroupMembersModal(false)}
                style={{
                  padding: '8px 16px',
                  background: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Modal */}
      {processing && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '40px',
              textAlign: 'center',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              minWidth: '320px',
            }}
          >
            <div style={{ marginBottom: '20px' }}>
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  border: '4px solid #e5e7eb',
                  borderTopColor: '#2563eb',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto',
                }}
              />
            </div>
            <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '600' }}>
              {processingMessage}
            </h3>
            <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
              Please wait...
            </p>
          </div>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}
    </div>
  );
}
